// Single source of truth for the support-v2 true-up job's option copy, reused
// by the per-team card and the migration-status row.

export type TrueupOptionKey =
  | "dryRun"
  | "matchVoice"
  | "noMigrateMissing"
  | "limit";

export interface TrueupOption {
  key: TrueupOptionKey;
  label: string;
  description: string;
  riskNote?: string;
}

export const TRUEUP_JOB_SUMMARY =
  "True-up walks this team's open v1 conversations and syncs each one into " +
  "support-v2: it matches the v1 conversation to its v2 ticket (by legacy id, " +
  "provider thread, content, or — optionally — customer voice), then brings the " +
  "v2 ticket into agreement with v1. Applying can reopen or snooze the v2 ticket " +
  "to match the v1 status, set the assignee (it never clears an assignee), set " +
  "or clear the read flag, and reconcile migration-owned tags while preserving " +
  "tags a human added in v2. For matches that are missing the legacy id it " +
  "stamps the v1 id onto the v2 ticket. Any open v1 conversation with no v2 " +
  "ticket is migrated — note that migrating can create an empty shell ticket if " +
  "the conversation's content is orphaned in v1.";

export const TRUEUP_OPTIONS_BY_KEY: Record<TrueupOptionKey, TrueupOption> = {
  dryRun: {
    key: "dryRun",
    label: "Preview (dry run)",
    description:
      "Read-only. Scans and reports the plan — how many conversations would be " +
      "matched, stamped, reconciled, or migrated — without writing anything.",
  },
  matchVoice: {
    key: "matchVoice",
    label: "Match voice tickets by customer",
    description:
      "Allow voice (phone) conversations to be matched to a v2 ticket by the " +
      "customer alone when no stronger signal exists.",
    riskNote:
      "Lower-confidence match — a customer with multiple voice tickets can be " +
      "matched to the wrong one.",
  },
  noMigrateMissing: {
    key: "noMigrateMissing",
    label: "Don't migrate missing conversations",
    description:
      "Skip creating v2 tickets for open v1 conversations that have no v2 " +
      "ticket. Only reconcile conversations that already exist in v2.",
  },
  limit: {
    key: "limit",
    label: "Conversation limit",
    description:
      "Cap how many open conversations are processed. Leave blank to process " +
      "all. Use a small value to test the job on a subset first.",
  },
};

const TRUEUP_OPTION_ORDER: readonly TrueupOptionKey[] = [
  "dryRun",
  "matchVoice",
  "noMigrateMissing",
  "limit",
];

export const TRUEUP_OPTIONS: readonly TrueupOption[] = TRUEUP_OPTION_ORDER.map(
  (key) => TRUEUP_OPTIONS_BY_KEY[key],
);

export type ReconcileOptionKey =
  | "dryRun"
  | "closeV2Only"
  | "dedupeOpen"
  | "matchVoice";

export interface ReconcileOption {
  key: ReconcileOptionKey;
  label: string;
  description: string;
  riskNote?: string;
}

export const RECONCILE_JOB_SUMMARY =
  "Reconcile brings this team's support-v2 tickets back into agreement with " +
  "v1 (the v2→v1 direction). It removes duplicate v2 tickets that share a " +
  "thread with a legacy twin, closes orphan v2 tickets that match a v1 " +
  "conversation which is already terminal, and syncs legacy-linked ticket " +
  "statuses down to v1. Applying soft-deletes duplicate tickets (detaching " +
  "their lane) and closes/archives tickets — it never reopens a ticket.";

export const RECONCILE_OPTIONS_BY_KEY: Record<
  ReconcileOptionKey,
  ReconcileOption
> = {
  dryRun: {
    key: "dryRun",
    label: "Preview (dry run)",
    description:
      "Read-only. Scans and reports the plan — how many tickets would be " +
      "deleted, closed, or collapsed — without writing anything.",
  },
  closeV2Only: {
    key: "closeV2Only",
    label: "Close orphans absent from v1",
    description:
      "Also close orphan v2 tickets that could not be matched to any v1 " +
      "conversation, on the assumption that v1 simply never had them.",
    riskNote:
      "Lower-confidence — if the matcher missed a real v1 conversation, this " +
      "can close a ticket that should stay open.",
  },
  dedupeOpen: {
    key: "dedupeOpen",
    label: "Collapse duplicate open tickets",
    description:
      "When several open v2 tickets map to one open v1 conversation, keep one " +
      "and close the rest.",
    riskNote: "Closes open tickets to leave a single ticket per conversation.",
  },
  matchVoice: {
    key: "matchVoice",
    label: "Match voice tickets by customer",
    description:
      "Allow voice (phone) conversations to be matched to a v1 conversation " +
      "by the customer alone when no stronger signal exists.",
    riskNote:
      "Lower-confidence match — a customer with multiple voice tickets can be " +
      "matched to the wrong one.",
  },
};

const RECONCILE_OPTION_ORDER: readonly ReconcileOptionKey[] = [
  "dryRun",
  "closeV2Only",
  "dedupeOpen",
  "matchVoice",
];

export const RECONCILE_OPTIONS: readonly ReconcileOption[] =
  RECONCILE_OPTION_ORDER.map((key) => RECONCILE_OPTIONS_BY_KEY[key]);

export const FINALIZE_JOB_SUMMARY =
  "Finalize chains the full cutover pipeline for each team: true-up (sync open " +
  "v1 conversations into v2), reconcile (close v2-only drift against v1), then " +
  "re-run count verification. When \u201cFlip flag on pass\u201d is enabled and " +
  "verification lands within tolerance, the team is removed from the " +
  "support_v1_holdout_team table (what production evaluates) and " +
  "verified by read-back. Teams that fail verification are left un-flipped with a " +
  "fresh count-verification row for this table.";

export const FINALIZE_FLIP_OPTION = {
  key: "flip" as const,
  label: "Flip flag on pass",
  description:
    "After true-up + reconcile, if count verification passes, clear the team " +
    "from the support_v1_holdout_team table and verify " +
    "(LaunchDarkly is mirrored best-effort).",
  riskNote: "Live cutover: the team's agents move to the v2 inbox immediately.",
};
